/**
 * Offline draft replay (§7.9, FR-42).
 *
 * Replays a finished draft pick by pick so the whole pipeline can be exercised
 * without waiting for a live one. This is M3's done-criterion — "replay of a
 * real completed draft shows correct strikethroughs at every pick" — and it is
 * the only practical way to test against *real* data: real names that stress
 * the matcher, real payloads with keepers and traded picks, real edge cases
 * that synthetic fixtures cannot contain (R2 forbids committing the real ones).
 *
 * The design is almost free, and deliberately so. `toDraftState` is already a
 * pure function of a pick list, so "the draft as of pick N" is just that
 * function applied to the first N picks. Replay therefore needs no parallel
 * state machine and cannot drift from live behaviour — if replay is correct,
 * live is correct, because they are the same code.
 *
 * Pure module — no I/O and no timers. The caller owns the clock.
 */

import type { DraftPick, DraftState, PlayerId, PlayerUniverse } from './types.ts';

export interface ReplayCursor {
  /** How many picks have "happened". 0 is the moment before pick 1. */
  index: number;
  /** Total picks in the completed draft. */
  total: number;
  /** The pick that just happened, or null at index 0. */
  current: DraftPick | null;
  /** The pick about to happen, or null once the draft is over. */
  next: DraftPick | null;
  done: boolean;
}

export function cursorAt(picks: DraftPick[], index: number): ReplayCursor {
  const clamped = Math.max(0, Math.min(index, picks.length));
  return {
    index: clamped,
    total: picks.length,
    current: clamped > 0 ? picks[clamped - 1] ?? null : null,
    next: picks[clamped] ?? null,
    done: clamped >= picks.length,
  };
}

/** The picks that have happened as of a cursor position. */
export function picksThrough(picks: DraftPick[], index: number): DraftPick[] {
  return picks.slice(0, Math.max(0, Math.min(index, picks.length)));
}

// ---------------------------------------------------------------------------
// Verification (M3's done-criterion)
// ---------------------------------------------------------------------------

export type AnomalyKind =
  | 'duplicate-pick'
  | 'unknown-player'
  | 'pick-number-gap'
  | 'slot-mismatch'
  | 'drafted-set-mismatch'
  | 'roster-mismatch';

export interface ReplayAnomaly {
  kind: AnomalyKind;
  pick_no: number;
  detail: string;
}

export interface ReplayVerification {
  draft_id: string;
  picks_checked: number;
  anomalies: ReplayAnomaly[];
  /** Players picked that the loaded universe does not contain. */
  unknown_players: PlayerId[];
  /** True when every pick produced exactly the expected board state. */
  ok: boolean;
}

export interface VerifyOptions {
  universe: PlayerUniverse;
  /** Builds the state as of the first N picks — normally `toDraftState`. */
  stateFor: (picks: DraftPick[]) => DraftState;
  /** Snake/linear slot expected for a given pick number, if checkable. */
  expectedSlot?: (pickNo: number) => number;
}

/**
 * Walk a completed draft and check that the board is right at *every* pick,
 * not just at the end.
 *
 * Checking only the final state would hide the failure mode that actually
 * matters on draft night: a board that is correct before and after but wrong
 * for the thirty seconds you were looking at it.
 */
export function verifyReplay(
  draftId: string,
  picks: DraftPick[],
  opts: VerifyOptions,
): ReplayVerification {
  const anomalies: ReplayAnomaly[] = [];
  const unknown = new Set<PlayerId>();
  const seen = new Set<PlayerId>();

  for (let i = 0; i < picks.length; i++) {
    const pick = picks[i]!;

    if (seen.has(pick.player_id)) {
      anomalies.push({
        kind: 'duplicate-pick',
        pick_no: pick.pick_no,
        detail: `${pick.player_id} was already drafted earlier in this draft.`,
      });
    }
    seen.add(pick.player_id);

    if (!opts.universe.has(pick.player_id)) {
      unknown.add(pick.player_id);
      anomalies.push({
        kind: 'unknown-player',
        pick_no: pick.pick_no,
        detail: `${pick.player_id} is not in the loaded player universe — the cache may be stale.`,
      });
    }

    // Sleeper numbers picks from 1 with no gaps. A gap means we are missing a
    // pick, which would silently under-report the board.
    if (pick.pick_no !== i + 1) {
      anomalies.push({
        kind: 'pick-number-gap',
        pick_no: pick.pick_no,
        detail: `Expected pick #${i + 1} but found #${pick.pick_no}.`,
      });
    }

    if (opts.expectedSlot) {
      const expected = opts.expectedSlot(pick.pick_no);
      if (expected !== pick.draft_slot) {
        anomalies.push({
          kind: 'slot-mismatch',
          pick_no: pick.pick_no,
          detail: `Pick #${pick.pick_no} belongs to slot ${expected} but is recorded against slot ${pick.draft_slot}.`,
        });
      }
    }

    // The load-bearing check: after N picks, the drafted set must be exactly
    // the first N players, no more and no fewer.
    const state = opts.stateFor(picksThrough(picks, i + 1));
    const drafted = new Set(state.picks.map((p) => p.player_id));

    if (drafted.size !== seen.size) {
      anomalies.push({
        kind: 'drafted-set-mismatch',
        pick_no: pick.pick_no,
        detail: `Board shows ${drafted.size} drafted players after pick #${pick.pick_no}; ${seen.size} have been picked.`,
      });
    } else {
      for (const id of seen) {
        if (!drafted.has(id)) {
          anomalies.push({
            kind: 'drafted-set-mismatch',
            pick_no: pick.pick_no,
            detail: `${id} was picked but is not struck through on the board.`,
          });
          break;
        }
      }
    }

    // The user's own roster must contain exactly their own picks so far.
    if (state.my_draft_slot !== null) {
      const mine = picksThrough(picks, i + 1).filter((p) => p.draft_slot === state.my_draft_slot);
      if (mine.length !== state.my_roster.length) {
        anomalies.push({
          kind: 'roster-mismatch',
          pick_no: pick.pick_no,
          detail: `Your roster shows ${state.my_roster.length} players; ${mine.length} picks were made from slot ${state.my_draft_slot}.`,
        });
      }
    }
  }

  return {
    draft_id: draftId,
    picks_checked: picks.length,
    anomalies,
    unknown_players: [...unknown],
    ok: anomalies.length === 0,
  };
}

// ---------------------------------------------------------------------------
// Playback speeds
// ---------------------------------------------------------------------------

export const REPLAY_SPEEDS = [
  { label: '1×', pickIntervalMs: 4000 },
  { label: '4×', pickIntervalMs: 1000 },
  { label: '20×', pickIntervalMs: 200 },
  { label: 'Instant', pickIntervalMs: 0 },
] as const;

export type ReplaySpeed = (typeof REPLAY_SPEEDS)[number];

/**
 * Format a one-line summary of a verification, for both the CLI and the UI.
 * Written to be readable by someone who has not read this file.
 */
export function summarizeVerification(v: ReplayVerification): string {
  if (v.ok) {
    return `Replayed ${v.picks_checked} picks with no anomalies — the board was correct at every pick.`;
  }
  const byKind = new Map<AnomalyKind, number>();
  for (const a of v.anomalies) byKind.set(a.kind, (byKind.get(a.kind) ?? 0) + 1);
  const parts = [...byKind.entries()].map(([kind, n]) => `${n} ${kind}`);
  return `Replayed ${v.picks_checked} picks with ${v.anomalies.length} anomalies (${parts.join(', ')}).`;
}
